import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Stack,
  Chip,
  Collapse,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { getWelcomeText } from '../../../utils/welcomeText';
import { wlog } from '../../../utils/welcomeDebug';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
  { value: 7, label: 'Dom' },
];

/**
 * Tela 8 — Expediente por Profissional
 * Reutilizada uma vez por profissional (recebe professionalIndex como prop)
 * Define dias e turnos (1 ou 2 turnos por dia)
 * Convenção: days = 1=Seg...7=Dom
 */
export function Step8Schedule({ wizard, professionalIndex, goNext }) {
  wlog('Step8Schedule', 'RENDER — INÍCIO', {
    professionalIndex,
    professionalsLen: wizard.state?.professionals?.length,
    templateId: wizard.template?.id || 'NENHUM',
  });
  const template = wizard.template;
  const professional = wizard.state.professionals[professionalIndex];
  const firstName = wizard.state.userName.split(' ')[0].trim();
  const profName = professional.name;

  // Estado local para monta a UI (dias e turnos)
  // Carrega do estado do wizard se já foi preenchido, senão usa defaults do template
  const existingSchedule = professional.schedule || null;

  // defaultScheduleShifts é sempre [{daysOfWeek, startTime, endTime, name}] em todos os templates.
  // Aceita também array simples de inteiros como fallback de segurança.
  const rawDefaultShifts = template?.onboarding?.defaultScheduleShifts;
  const templateShifts = Array.isArray(rawDefaultShifts)
    ? (typeof rawDefaultShifts[0] === 'object' ? rawDefaultShifts : [])
    : [];
  // Dias pré-selecionados: união de todos os daysOfWeek do template
  const defaultSelectedDays = templateShifts.length > 0
    ? [...new Set(templateShifts.flatMap((s) => s.daysOfWeek || []))]
    : (Array.isArray(rawDefaultShifts) ? rawDefaultShifts : [1, 2, 3, 4, 5]);

  // Copy temática carregada assincronamente
  const [scheduleTitle, setScheduleTitle] = useState('');
  const [scheduleHint, setScheduleHint] = useState('');
  // Erro de validação dos turnos (hora vazia / início >= término)
  const [timeError, setTimeError] = useState('');

  // Defaults dos turnos, usados como fallback quando o usuário apaga o campo
  const shift1StartDefault = templateShifts[0]?.startTime || '09:00';
  const shift1EndDefault = templateShifts[0]?.endTime || '12:00';
  const shift2StartDefault = templateShifts[1]?.startTime || '13:00';
  const shift2EndDefault = templateShifts[1]?.endTime || '18:00';

  // Input type="time" entrega "HH:mm" ou "" (quando apagado)
  const isValidTime = (t) => typeof t === 'string' && /^\d{2}:\d{2}$/.test(t);

  const [selectedDays, setSelectedDays] = useState(
    existingSchedule?.shifts?.[0]?.days || defaultSelectedDays
  );
  const [twoShifts, setTwoShifts] = useState(
    existingSchedule?.shifts?.length > 1 || (templateShifts.length > 1 && !existingSchedule)
  );
  const [shift1Start, setShift1Start] = useState(
    existingSchedule?.shifts?.[0]?.startTime || templateShifts[0]?.startTime || '09:00'
  );
  const [shift1End, setShift1End] = useState(
    existingSchedule?.shifts?.[0]?.endTime || templateShifts[0]?.endTime || '12:00'
  );
  const [shift2Start, setShift2Start] = useState(
    existingSchedule?.shifts?.[1]?.startTime || templateShifts[1]?.startTime || '13:00'
  );
  const [shift2End, setShift2End] = useState(
    existingSchedule?.shifts?.[1]?.endTime || templateShifts[1]?.endTime || '18:00'
  );

  useEffect(() => {
    // Carrega copy do tema
    (async () => {
      const title = await getWelcomeText(template, 'scheduleTitle', { 
        firstName, 
        professionalName: profName,
      });
      const hint = await getWelcomeText(template, 'scheduleHint', { firstName });
      
      setScheduleTitle(title || `Qual o expediente de ${profName}?`);
      setScheduleHint(hint || 'Dá pra criar bloqueios e exceções depois. Deixe certinho agora para gerar os agendamentos.');
    })();
    // NÃO focar o título via querySelector: durante a animação de transform
    // do trilho, o focus() rola o slide para fora da vista → tela branca.
  }, [professionalIndex, template, firstName, profName]);

  const handleDayToggle = (event, newDays) => {
    if (newDays.length > 0) {
      setSelectedDays(newDays);
    }
  };

  const handleContinue = () => {
    // Guarda: turnos ativos precisam de horas válidas com início antes do término.
    // Sem isto, um campo apagado ('') seguia pro backend e reprovava o welcome INTEIRO (400),
    // travando o onboarding num loop cego. Bloqueia o avanço e aponta o problema aqui mesmo.
    const activeShifts = [{ label: 'Turno 1', start: shift1Start, end: shift1End }];
    if (twoShifts) {
      activeShifts.push({ label: 'Turno 2', start: shift2Start, end: shift2End });
    }
    for (const t of activeShifts) {
      if (!isValidTime(t.start) || !isValidTime(t.end)) {
        setTimeError(`Preencha as horas de início e término do ${t.label}.`);
        return;
      }
      // Comparação lexicográfica de "HH:mm" equivale à ordem cronológica.
      if (t.start >= t.end) {
        setTimeError(`No ${t.label}, a hora de início deve ser antes do término.`);
        return;
      }
    }
    setTimeError('');

    // Monta o objeto de expediente no formato esperado
    // { shifts: [{ days: [...], startTime, endTime }, ...] }
    const shifts = [];

    // Turno 1: sempre existe se há dias selecionados
    if (selectedDays.length > 0) {
      shifts.push({
        days: selectedDays,
        startTime: shift1Start,
        endTime: shift1End,
      });
    }

    // Turno 2: só se "2 turnos" foi selecionado
    if (twoShifts) {
      shifts.push({
        days: selectedDays,
        startTime: shift2Start,
        endTime: shift2End,
      });
    }

    wizard.setProfessionalSchedule(professionalIndex, { shifts });
    goNext();
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100%',
        py: 4,
      }}
      data-step={`schedule_${professionalIndex}`}
    >
      <Box component="section" sx={{ width: '100%' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '24px', md: '28px' },
            fontWeight: 'bold',
            mb: 1,
            color: 'text.primary',
          }}
          tabIndex={-1}
        >
          {scheduleTitle}
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', mb: 4 }}
        >
          {scheduleHint}
        </Typography>

        {/* Seleção de dias */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Dias da semana
        </Typography>
        <ToggleButtonGroup
          value={selectedDays}
          onChange={handleDayToggle}
          aria-label="dias da semana"
          sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}
        >
          {DAYS_OF_WEEK.map((day) => {
            const isSelected = selectedDays.includes(day.value);
            return (
              <ToggleButton
                key={day.value}
                value={day.value}
                aria-label={day.label}
                sx={{
                  flex: '1 1 calc(50% - 8px)',
                  minWidth: '60px',
                  gap: 0.75,
                  border: '2px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  bgcolor: isSelected ? 'primary.main' : 'background.paper',
                  color: isSelected ? '#fff' : 'text.primary',
                  fontWeight: isSelected ? 700 : 400,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: '#fff',
                    borderColor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark', borderColor: 'primary.dark' },
                  },
                  '&:hover': {
                    bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                    borderColor: isSelected ? 'primary.dark' : 'primary.light',
                  },
                  transition: 'all 0.15s ease',
                }}
              >
                {isSelected && (
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>✓</Box>
                )}
                {day.label}
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>

        {/* Turnos */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Turnos
        </Typography>

        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={!twoShifts}
                onChange={(e) => setTwoShifts(!e.target.checked)}
              />
            }
            label={twoShifts ? '2 turnos' : '1 turno'}
          />
        </Box>

        {/* Turno 1 */}
        <Box sx={{ p: 2, mb: 3, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
            Turno 1
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <TextField
              type="time"
              value={shift1Start}
              onChange={(e) => { setShift1Start(e.target.value); setTimeError(''); }}
              onBlur={() => { if (!shift1Start) setShift1Start(shift1StartDefault); }}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Typography variant="body2" sx={{ textAlign: 'center', flexShrink: 0 }}>até</Typography>
            <TextField
              type="time"
              value={shift1End}
              onChange={(e) => { setShift1End(e.target.value); setTimeError(''); }}
              onBlur={() => { if (!shift1End) setShift1End(shift1EndDefault); }}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Stack>
        </Box>

        {/* Turno 2 (se 2 turnos) */}
        <Collapse in={twoShifts}>
          <Box sx={{ p: 2, mb: 3, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
              Turno 2
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <TextField
                type="time"
                value={shift2Start}
                onChange={(e) => { setShift2Start(e.target.value); setTimeError(''); }}
                onBlur={() => { if (!shift2Start) setShift2Start(shift2StartDefault); }}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Typography variant="body2" sx={{ textAlign: 'center', flexShrink: 0 }}>até</Typography>
              <TextField
                type="time"
                value={shift2End}
                onChange={(e) => { setShift2End(e.target.value); setTimeError(''); }}
                onBlur={() => { if (!shift2End) setShift2End(shift2EndDefault); }}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Stack>
          </Box>
        </Collapse>

        {timeError && (
          <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 600 }}>
            {timeError}
          </Typography>
        )}

        <Button
          variant="contained"
          size="large"
          onClick={handleContinue}
          sx={{
            width: '100%',
            py: 1.5,
            mt: 2,
          }}
        >
          Continuar
        </Button>
      </Box>
    </Container>
  );
}

export default Step8Schedule;
